/**
 * Per-form email invitations (/api/admin/forms/:id/invitations): upload a
 * list, every person gets a private link, the batch table shows who opened
 * and who finished.
 */
import { apiFetch } from './client';
import type { SendWindow, CsvUploadResult } from './marketing';

export type InvitationBatchStatus = 'draft' | 'sending' | 'paused' | 'done';

export interface InvitationBatch {
  id: number;
  form_id: number;
  subject: string;
  message: string;
  status: InvitationBatchStatus;
  /** the reminder schedule (R7): days after each person's first email, at most three */
  reminder_days: number[];
  created_at: string;
  updated_at: string;
}

export interface InvitationBatchCounts {
  total: number;
  sent: number;
  failed: number;
  pending: number;
  opened: number;
  submitted: number;
  /** reached by the first email, not finished — who "Remind unfinished" writes to */
  unfinished: number;
  /** reminded at least once */
  reminded: number;
  /** reminders waiting for the outbox */
  reminders_queued: number;
}

export type InvitationBatchRow = InvitationBatch & InvitationBatchCounts;

/** One reminder to one person (R7): queued, sent, or failed. */
export interface InvitationReminder {
  id: number;
  recipient_id: number;
  batch_id: number;
  kind: 'once' | 'schedule';
  day: number | null;
  sent_at: string | null;
  message_id: string | null;
  failed_at: string | null;
  error: string | null;
  created_at: string;
}

/** The schedule's bounds, as the server enforces them. */
export const MAX_REMINDER_STEPS = 3;
export const MAX_REMINDER_DAY = 30;

export interface InvitationRecipient {
  id: number;
  email: string;
  name: string | null;
  sent_at: string | null;
  message_id: string | null;
  failed_at: string | null;
  error: string | null;
  opened_count: number;
  first_opened_at: string | null;
  submitted_at: string | null;
  reminder_count?: number;
  last_reminded_at?: string | null;
}

export const listInvitationBatchesApi = (formId: number) =>
  apiFetch<{
    success: boolean; batches: InvitationBatchRow[]; window: SendWindow;
    smtpConfigured: boolean; published: boolean;
  }>(`/admin/forms/${formId}/invitations`);

export const createInvitationBatchApi = (formId: number, body: { subject: string; message: string; reminderDays?: number[] }) =>
  apiFetch<{ success: boolean; batch: InvitationBatch }>(`/admin/forms/${formId}/invitations`, {
    method: 'POST', body: JSON.stringify(body),
  });

export const getInvitationBatchApi = (formId: number, batchId: number) =>
  apiFetch<{
    success: boolean; batch: InvitationBatch; recipients: InvitationRecipient[];
    reminders?: InvitationReminder[]; counts?: InvitationBatchCounts;
    window: SendWindow; smtpConfigured: boolean; published?: boolean;
  }>(`/admin/forms/${formId}/invitations/${batchId}`);

export const updateInvitationBatchApi = (formId: number, batchId: number, body: { subject?: string; message?: string; reminderDays?: number[] }) =>
  apiFetch<{ success: boolean; batch: InvitationBatch }>(`/admin/forms/${formId}/invitations/${batchId}`, {
    method: 'PATCH', body: JSON.stringify(body),
  });

export const deleteInvitationBatchApi = (formId: number, batchId: number) =>
  apiFetch<{ success: boolean }>(`/admin/forms/${formId}/invitations/${batchId}`, { method: 'DELETE' });

export const uploadInvitationCsvApi = (formId: number, batchId: number, file: File | Blob) => {
  const body = new FormData();
  body.append('file', file, file instanceof File ? file.name : 'recipients.csv');
  return apiFetch<CsvUploadResult>(`/admin/forms/${formId}/invitations/${batchId}/recipients`, { method: 'POST', body });
};

export const removeInvitationRecipientApi = (formId: number, batchId: number, recipientId: number) =>
  apiFetch<{ success: boolean }>(`/admin/forms/${formId}/invitations/${batchId}/recipients/${recipientId}`, { method: 'DELETE' });

export const testInvitationBatchApi = (formId: number, batchId: number) =>
  apiFetch<{ success: boolean; to: string }>(`/admin/forms/${formId}/invitations/${batchId}/test`, { method: 'POST' });

export const startInvitationBatchApi = (formId: number, batchId: number) =>
  apiFetch<{ success: boolean; batch: InvitationBatch }>(`/admin/forms/${formId}/invitations/${batchId}/start`, { method: 'POST' });

export const pauseInvitationBatchApi = (formId: number, batchId: number) =>
  apiFetch<{ success: boolean; batch: InvitationBatch }>(`/admin/forms/${formId}/invitations/${batchId}/pause`, { method: 'POST' });

/** "Remind unfinished" (R7): queue one reminder for everybody the first email reached who has not finished. */
export const remindInvitationBatchApi = (formId: number, batchId: number) =>
  apiFetch<{ success: boolean; queued: number; unfinished: number; skipped: number; paused: boolean }>(
    `/admin/forms/${formId}/invitations/${batchId}/remind`, { method: 'POST' });
